import type { DesignCanvas, DesignDocument, Layer } from '../document/types.ts'
import { cloneDocument, fullBleedFrame } from '../document/defaults.ts'

/**
 * Dokument auf ein anderes Format umrechnen (Größenwechsel in der Sidebar).
 * Regel: Mittelpunkt jedes Layers bleibt relativ zur Trim-Box erhalten; Breite/Höhe und
 * Textgrößen skalieren mit dem Diagonalen-Verhältnis (nicht linear zur Breite, sonst wird
 * Text bei Panorama absurd groß). `full-bleed`-Layer schnappen auf die neue Beschnitt-Box.
 * `fittedSizePt` wird gelöscht (muss neu gemessen werden).
 */
export function rescaleDocument(doc: DesignDocument, nextCanvas: DesignCanvas): DesignDocument {
  const out = cloneDocument(doc)
  const prev = doc.canvas
  const oldW = Math.max(1e-6, prev.widthMm)
  const oldH = Math.max(1e-6, prev.heightMm)
  const sx = nextCanvas.widthMm / oldW
  const sy = nextCanvas.heightMm / oldH
  const s = Math.hypot(nextCanvas.widthMm, nextCanvas.heightMm) / Math.hypot(oldW, oldH)

  out.canvas = { ...nextCanvas, background: prev.background }
  out.layers = out.layers.map((layer): Layer => {
    if (layer.constraints.role === 'full-bleed') {
      return { ...layer, frame: fullBleedFrame(nextCanvas) }
    }
    const f = layer.frame
    const cx = (f.xMm + f.wMm / 2) * sx
    const cy = (f.yMm + f.hMm / 2) * sy
    const w = f.wMm * s
    const h = f.hMm * s
    const frame = { ...f, xMm: cx - w / 2, yMm: cy - h / 2, wMm: w, hMm: h }
    if (layer.type === 'text') {
      const { fittedSizePt: _drop, ...rest } = layer
      return { ...rest, frame, font: { ...layer.font, sizePt: round2(layer.font.sizePt * s) } }
    }
    if (layer.type === 'image' && layer.mask.kind === 'rounded') {
      return { ...layer, frame, mask: { kind: 'rounded', radiusMm: round2(layer.mask.radiusMm * s) } }
    }
    if (layer.type === 'shape') {
      return { ...layer, frame, cornerRadiusMm: round2(layer.cornerRadiusMm * s) }
    }
    return { ...layer, frame }
  })
  return out
}

function round2(v: number): number {
  return Math.round(v * 100) / 100
}
